import React, { useState, ChangeEvent, useEffect } from 'react';
import axios from 'axios';
import randomString from '@/lib/utils/randomString';
import { Trash2 } from "lucide-react";
interface UploadProgress {
  [key: string]: number;
}

interface FileDetails {
  name: string;
  content_type: string;
  size: number;
  path: string;
  url: string;
  original_name: string;
}

interface FileUploadProps {
  files: FileDetails[];
  onChange: (files: FileDetails[]) => void;
  disabled?: boolean;
  setAttachmentUploading?: (uploading: boolean) => void;
}

const FileUpload: React.FC<FileUploadProps> = ({ files = [], onChange, disabled, setAttachmentUploading }) => {
  const folderName = 'uploads';
  const [selectedFiles, setSelectedFiles] = useState<FileDetails[]>(files);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<UploadProgress>({});
  const [invalidFiles, setInvalidFiles] = useState<string[]>([]);

  useEffect(() => {
    setSelectedFiles(files);
  }, [files]);

  const getCsrfToken = () => {
    const tokenMeta = document.querySelector('meta[name="csrf-token"]');
    return tokenMeta ? tokenMeta.getAttribute('content') || '' : '';
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const fileArray = Array.from(event.target.files);
      setInvalidFiles([]);

      const newFileDetails = await Promise.all(
        fileArray.map(async (file) => {
          const details = await uploadFileToS3(file);
          if (details) {
            return {
              name: details.name,
              content_type: file.type,
              size: file.size,
              original_name: details.original_name,
              path: details.path,
              url: details.url,
            };
          } else {
            setInvalidFiles((prev) => [...prev, file.name]); // Hata alan dosyaları listeye ekle
            return null;
          }
        })
      );
      
      const validFiles: FileDetails[] = newFileDetails.filter((file): file is FileDetails => file !== null);

      setUploading(false);
      if (setAttachmentUploading) {
        setAttachmentUploading(false);
      }
      const updatedFiles = [...selectedFiles, ...validFiles];
      setSelectedFiles(updatedFiles);
      onChange(updatedFiles);

      event.target.value = "";
    }
  };
  const uploadFileToS3 = async (file: File): Promise<{url: string, path: string, name: string, original_name: string, size: number, content_type: string } | null> => {
    try {
      setUploading(true);
      if (setAttachmentUploading) {
        setAttachmentUploading(true);
      }
      const csrfToken = getCsrfToken();
      const fileNameParts = file.name.split('.');
      const fileExtension = fileNameParts.length > 1 ? fileNameParts.pop() : '';
      const fileNameWithoutExtension = fileNameParts.join('.');


      const fileName = `${fileNameWithoutExtension}${randomString(6)}.${fileExtension}`;
      const fileOriginalName = file.name;

      const response = await axios.post('/attachments/presigned_url', {
        file_name: fileName,
        file_type: file.type,
        folder_name: folderName
      }, {
        headers: {
          'X-CSRF-Token': csrfToken,
        },
      });

      if (response.data.error) {
        console.error('File type not allowed:', response.data.error);
        return null;
      }

      const presignedUrl: string = response.data.url;

      // Upload the file to S3
      await axios.put(presignedUrl, file, {
        headers: {
          'Content-Type': file.type,
        },
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const percentCompleted = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            );
            setUploadProgress((prevProgress) => ({
              ...prevProgress,
              [file.name]: percentCompleted,
            }));
          }
        },
      });

      return {
        url: presignedUrl.split('?')[0],
        path: `${folderName}/${fileName}`,
        name: fileName,
        original_name: fileOriginalName,
        size: file.size,
        content_type: file.type,
      };

    } catch (error) {
      console.error('file upload error:', error);
      return null;
    }
  };

  const handleDelete = (fileToDelete: FileDetails) => {
    const updatedFiles = selectedFiles.filter(file => file.name !== fileToDelete.name);
    setSelectedFiles(updatedFiles);
    onChange(updatedFiles);
  };

  const handleUpload = async () => {
    setUploading(true);
    if (setAttachmentUploading) {
      setAttachmentUploading(true);
    }
    const uploadedFiles = await Promise.all(
      files.map(file => uploadFileToS3(file as unknown as File))
    );

    setUploading(false);
    if (setAttachmentUploading) {
      setAttachmentUploading(false);
    }
  };

  return (
    <div>
      <input
        type="file"
        multiple
        onChange={handleFileChange}
        disabled={uploading || disabled }
        className="file:cursor-pointer file:text-sm file:font-medium dark:file:text-darkTextColor disabled:file:cursor-not-allowed"
      />
      <button onClick={handleUpload} disabled={uploading || files.length === 0}>
        {uploading ? 'Uploading...' : ''}
      </button>

      <div>
        {files.map((file, index) => (
          <div key={`${file.name}-${file.size}-${index}`} className="flex">
            <span>{file.original_name} ({file.size} bytes)</span>
            <Trash2
              size={16}
              className="ml-4 mt-1 cursor-pointer text-red-500 hover:text-red-700"
              onClick={() => handleDelete(file)}
            />
          </div>
        ))}
      </div>

      {invalidFiles.length > 0 && (
        <div className="mt-4 p-3 bg-red-100 border border-border text-red-700 rounded">
          <strong>Unauthorized file type detected</strong>
          <ul className="list-disc pl-5">
            {invalidFiles.map((file, index) => (
              <li key={index}>{file}</li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default FileUpload;
